import React, { useCallback, useEffect, useState } from 'react';
import { getSiteRoutes, syncSiteRoutes, setSiteRouteSearchable } from './api';
import Alert from './ui/Alert';
import Badge from './ui/Badge';
import { relativeTime } from './domainMetrics';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/* Pages list card: content pages the site's templates render (no DAM page),
   registered from the live sitemap so topic analysis knows their age and site
   search can find them. The site never reads these records; creating a DAM page
   with the same slug takes the page over and the next sync drops the route. */
export default function TemplateRoutes({ domainId, liveUrl, onChange }) {
  const [routes, setRoutes] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => getSiteRoutes(domainId)
    .then((d) => setRoutes(d.routes || []))
    .catch(() => setRoutes([])), [domainId]);

  useEffect(() => { load(); }, [load]);

  const run = async (apply) => {
    setBusy(true);
    setError(null);
    try {
      const r = await syncSiteRoutes(domainId, apply);
      setPreview(apply ? null : r);
      if (apply) {
        await load();
        if (onChange) onChange();
        if (r.failed?.length) setError(`Could not fetch ${plural(r.failed.length, 'page')}: ${r.failed.join(', ')}`);
      }
    } catch (err) {
      setError(errText(err, 'Sync failed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleSearch = async (route) => {
    try {
      const r = await setSiteRouteSearchable(domainId, route.id, !route.searchable);
      setRoutes((rows) => rows.map((x) => (x.id === route.id ? { ...x, searchable: r.searchable } : x)));
      if (onChange) onChange();
    } catch (err) {
      setError(errText(err, 'Failed to save'));
    }
  };

  if (routes === null) return null;
  const changes = preview ? preview.add.length + preview.refresh.length + preview.drop.length : 0;

  return (
    <div className="card" style={{ marginTop: '16px', padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '240px' }}>
          <strong>Template pages</strong>{' '}
          <span className="muted small">
            {routes.length
              ? `${plural(routes.length, 'page')} the site's templates render · ${routes.filter((r) => r.searchable).length} in site search`
              : 'Content pages in the sitemap that the site renders from templates, not DAM pages'}
          </span>
        </div>
        {routes.length > 0 && (
          <button type="button" className="btn btn-secondary" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Show'}
          </button>
        )}
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => run(false)}>
          {busy && !preview ? 'Checking…' : 'Sync from sitemap'}
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {preview && (
        <div style={{ marginTop: '12px', fontSize: '13px' }}>
          {changes === 0 ? (
            <span className="muted">Up to date: no template pages to add or drop.</span>
          ) : (
            <>
              <div>
                {plural(preview.add.length, 'new page')} · {plural(preview.refresh.length, 'snapshot')} to refresh
                · {plural(preview.drop.length, 'route')} to drop
                {preview.taken_over.length > 0 && ` (${preview.taken_over.length} now DAM pages)`}
              </div>
              {preview.add.length > 0 && (
                <div className="muted mono" style={{ fontSize: '12px', marginTop: '4px' }}>
                  {preview.add.map((s) => `/${s}/`).join('  ')}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(true)}>
                  {busy ? 'Syncing…' : 'Apply'}
                </button>
                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setPreview(null)}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      {open && routes.length > 0 && (
        <div className="table-wrap" style={{ marginTop: '12px' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Page</th>
                <th style={{ textAlign: 'right' }}>Words</th>
                <th>Site search</th>
                <th>Snapshot</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={r.id}>
                  <td>
                    <a href={`${liveUrl}/${r.slug}/`} target="_blank" rel="noopener noreferrer">
                      <strong>{r.title || r.slug}</strong>
                    </a>{' '}
                    <Badge state="info" title="Rendered by the site's template; edit it in the site's code">Template</Badge>
                    <div className="muted mono" style={{ fontSize: '12px' }}>/{r.slug}/</div>
                  </td>
                  <td style={{ textAlign: 'right' }}>{r.words.toLocaleString()}</td>
                  <td>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
                      <input type="checkbox" checked={r.searchable} onChange={() => toggleSearch(r)} />
                      <span className="small">{r.searchable ? 'Included' : 'Excluded'}</span>
                    </label>
                  </td>
                  <td className="muted small">{relativeTime(r.fetched_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
